import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { ArrowRight, ArrowDown } from "@phosphor-icons/react";
import { useLang } from "@/context/LanguageContext";
import { media, contacts } from "@/data/content";
import { scrollToId } from "@/components/Reveal";

export const Hero = () => {
  const { t } = useLang();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  const stagger = {
    hidden: {},
    show: { transition: { staggerChildren: 0.12, delayChildren: 0.3 } },
  };
  const item = {
    hidden: { opacity: 0, y: 40 },
    show: { opacity: 1, y: 0, transition: { duration: 1, ease: [0.16, 1, 0.3, 1] } },
  };

  return (
    <section id="hero" ref={ref} data-testid="hero" className="relative h-[100svh] w-full overflow-hidden">
      <motion.div style={{ y, scale }} className="absolute inset-0">
        <img src={media.hero} alt="Interni Zen Home" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-b from-black/25 via-black/10 to-black/45" />
      </motion.div>

      <motion.div
        style={{ opacity }}
        className="relative z-10 h-full max-w-[1400px] mx-auto px-6 md:px-12 flex flex-col justify-center"
      >
        <motion.div variants={stagger} initial="hidden" animate="show" className="max-w-4xl">
          <motion.p
            variants={item}
            className="text-xs sm:text-sm tracking-luxe uppercase text-white/85 mb-6"
          >
            {t.hero.kicker}
          </motion.p>
          <motion.h1
            variants={item}
            className="font-display text-white font-light leading-[0.95] tracking-tight text-5xl sm:text-6xl lg:text-7xl"
          >
            {t.hero.title}
          </motion.h1>
          <motion.p
            variants={item}
            className="mt-8 text-white/90 text-base sm:text-lg max-w-xl leading-relaxed"
          >
            {t.hero.subtitle}
          </motion.p>
          <motion.div variants={item} className="mt-10 flex flex-col sm:flex-row gap-4">
            <button
              data-testid="hero-cta-primary"
              onClick={() => scrollToId("contact")}
              className="group inline-flex items-center justify-center gap-2 bg-[#FAFAF7] text-[#1A1A1A] px-8 py-4 text-sm tracking-wide hover:bg-[#8B7355] hover:text-white transition-colors duration-300"
            >
              {t.hero.ctaPrimary}
              <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
            </button>
            <a
              data-testid="hero-cta-brochure"
              href={contacts.brochure}
              download
              className="group inline-flex items-center justify-center gap-2 border border-white/60 text-white px-8 py-4 text-sm tracking-wide hover:bg-white hover:text-[#1A1A1A] transition-colors duration-300"
            >
              {t.hero.ctaSecondary}
              <ArrowDown size={16} className="transition-transform duration-300 group-hover:translate-y-1" />
            </a>
          </motion.div>
        </motion.div>
      </motion.div>

      <motion.button
        style={{ opacity }}
        onClick={() => scrollToId("about")}
        data-testid="scroll-indicator"
        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-3 text-white/80"
      >
        <span className="text-[10px] tracking-luxe uppercase">{t.hero.scroll}</span>
        <span className="relative h-10 w-[1px] bg-white/40 overflow-hidden">
          <span className="scroll-dot absolute top-0 left-0 h-2 w-[1px] bg-white" />
        </span>
      </motion.button>
    </section>
  );
};
